import { collection, doc, setDoc, deleteDoc, query, onSnapshot, Unsubscribe, getDocs } from 'firebase/firestore';
import { db, auth } from './firebase';
import { CustomerUser } from '../types';
import { perfTracker } from './perfLogger';

const USERS_COLLECTION = 'users';

const userSubscribers = new Set<(users: CustomerUser[]) => void>();
let activeUsersUnsub: Unsubscribe | null = null;
const authoritativeUsersMap = new Map<string, CustomerUser>();

// Initialize from local cache if present
try {
  const saved = localStorage.getItem('rh_users_cache');
  if (saved) {
    const list: CustomerUser[] = JSON.parse(saved);
    if (Array.isArray(list)) {
      list.forEach((u) => {
        const key = (u.email || '').toLowerCase().trim() || u.id;
        if (key) authoritativeUsersMap.set(key, u);
      });
    }
  }
} catch {}

function notifySubscribers() {
  const fullList = Array.from(authoritativeUsersMap.values());
  try {
    localStorage.setItem('rh_users_cache', JSON.stringify(fullList));
  } catch {}
  userSubscribers.forEach((cb) => {
    try { cb(fullList); } catch {}
  });
}

export async function fetchUsersFromApi(): Promise<CustomerUser[]> {
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'x-admin-access': 'true',
      'x-rh-admin': 'true'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch('/api/admin/users', {
      headers,
      signal: AbortSignal.timeout(6000)
    });
    if (res.ok) {
      const data = await res.json();
      if (data.success && Array.isArray(data.users)) {
        const mapped: CustomerUser[] = data.users.map((u: any) => ({
          id: u.uid || u.id,
          name: u.name || u.displayName || 'User',
          email: (u.email || '').toLowerCase().trim(),
          avatar: u.avatar || u.photoURL || '',
          joinedDate: u.memberSince || u.joinedDate || new Date().toISOString().split('T')[0],
          totalOrders: Number(u.totalOrders) || 0,
          totalSpent: Number(u.totalSpent) || 0,
          status: (u.status === 'Blocked' || u.accountLocked === true) ? 'Blocked' : 'Active'
        }));

        mapped.forEach((u) => {
          const key = (u.email || '').toLowerCase().trim() || u.id;
          const existing = authoritativeUsersMap.get(key) || authoritativeUsersMap.get(u.id);
          authoritativeUsersMap.set(key, { ...existing, ...u });
        });

        notifySubscribers();
        return Array.from(authoritativeUsersMap.values());
      }
    }
  } catch (e) {
    console.warn('fetchUsersFromApi note:', e);
  }
  return Array.from(authoritativeUsersMap.values());
}

export async function fetchUsersFromFirestore(): Promise<CustomerUser[]> {
  const startTime = performance.now();
  try {
    const q = query(collection(db, USERS_COLLECTION));
    const snapshot = await getDocs(q);
    const duration = performance.now() - startTime;
    perfTracker.logFetch(USERS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'server', snapshot.size);

    snapshot.forEach((docSnap) => {
      const data = docSnap.data();
      const email = (data.email || '').toLowerCase().trim();
      const key = email || docSnap.id;
      const existing = authoritativeUsersMap.get(key) || authoritativeUsersMap.get(docSnap.id);
      authoritativeUsersMap.set(key, {
        ...existing,
        id: docSnap.id,
        name: data.name || data.displayName || existing?.name || 'User',
        email: email || data.email || existing?.email || '',
        avatar: data.avatar || data.photoURL || existing?.avatar || '',
        joinedDate: data.memberSince || data.joinedDate || existing?.joinedDate || new Date().toISOString().split('T')[0],
        totalOrders: data.totalOrders != null ? Number(data.totalOrders) : (existing?.totalOrders || 0),
        totalSpent: data.totalSpent != null ? Number(data.totalSpent) : (existing?.totalSpent || 0),
        status: (data.status === 'Blocked' || data.accountLocked === true || existing?.status === 'Blocked') ? 'Blocked' : 'Active'
      });
    });

    // Merge orders to ensure all ordering customers appear in customer list
    try {
      const ordersSnap = await getDocs(query(collection(db, 'orders')));
      const orderCounts = new Map<string, number>();
      const orderSpents = new Map<string, number>();

      ordersSnap.forEach((orderSnap) => {
        const o = orderSnap.data();
        const oEmail = (o.customerEmail || o.userEmail || '').toLowerCase().trim();
        if (oEmail) {
          orderCounts.set(oEmail, (orderCounts.get(oEmail) || 0) + 1);
          orderSpents.set(oEmail, (orderSpents.get(oEmail) || 0) + (Number(o.total) || 0));

          if (!authoritativeUsersMap.has(oEmail)) {
            authoritativeUsersMap.set(oEmail, {
              id: o.userId || oEmail.replace(/[^a-z0-9]/g, '_'),
              name: o.customerName || oEmail.split('@')[0],
              email: oEmail,
              avatar: '',
              joinedDate: o.date ? String(o.date).split('T')[0] : (o.createdAt ? String(o.createdAt).split('T')[0] : new Date().toISOString().split('T')[0]),
              totalOrders: 1,
              totalSpent: Number(o.total) || 0,
              status: 'Active'
            });
          }
        }
      });

      // Update counts
      for (const [email, user] of authoritativeUsersMap.entries()) {
        const count = orderCounts.get(email);
        const spent = orderSpents.get(email);
        if (count !== undefined && count > user.totalOrders) user.totalOrders = count;
        if (spent !== undefined && spent > user.totalSpent) user.totalSpent = spent;
      }
    } catch (e) {
      console.warn('Orders merge in fetchUsers warning:', e);
    }

    // Parallel refresh from Admin API to ensure Firebase Auth registered users are loaded
    fetchUsersFromApi().catch(() => {});

    notifySubscribers();
    return Array.from(authoritativeUsersMap.values());
  } catch (err) {
    console.warn('Fetch users note, trying API fallback:', err);
    return await fetchUsersFromApi();
  }
}

export function subscribeToUsersInFirestore(onUsersUpdate: (users: CustomerUser[]) => void): () => void {
  userSubscribers.add(onUsersUpdate);

  // Emit current authoritative list immediately
  const initial = Array.from(authoritativeUsersMap.values());
  if (initial.length > 0) {
    onUsersUpdate(initial);
  }

  // Trigger parallel API sync to load all Auth accounts
  fetchUsersFromApi().then((apiUsers) => {
    if (apiUsers && apiUsers.length > 0) {
      onUsersUpdate(apiUsers);
    }
  }).catch(() => {});

  if (!activeUsersUnsub) {
    perfTracker.registerListenerStart(USERS_COLLECTION);
    const q = query(collection(db, USERS_COLLECTION));

    activeUsersUnsub = onSnapshot(
      q,
      async (snapshot) => {
        const startTime = performance.now();
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          const email = (data.email || '').toLowerCase().trim();
          const key = email || docSnap.id;
          const existing = authoritativeUsersMap.get(key) || authoritativeUsersMap.get(docSnap.id);
          authoritativeUsersMap.set(key, {
            ...existing,
            id: docSnap.id,
            name: data.name || data.displayName || existing?.name || 'User',
            email: email || data.email || existing?.email || '',
            avatar: data.avatar || data.photoURL || existing?.avatar || '',
            joinedDate: data.memberSince || data.joinedDate || existing?.joinedDate || new Date().toISOString().split('T')[0],
            totalOrders: data.totalOrders != null ? Number(data.totalOrders) : (existing?.totalOrders || 0),
            totalSpent: data.totalSpent != null ? Number(data.totalSpent) : (existing?.totalSpent || 0),
            status: (data.status === 'Blocked' || data.accountLocked === true || existing?.status === 'Blocked') ? 'Blocked' : 'Active'
          });
        });

        // Merge orders in realtime snapshot
        try {
          const ordersSnap = await getDocs(query(collection(db, 'orders')));
          ordersSnap.forEach((orderSnap) => {
            const o = orderSnap.data();
            const oEmail = (o.customerEmail || o.userEmail || '').toLowerCase().trim();
            if (oEmail) {
              if (!authoritativeUsersMap.has(oEmail)) {
                authoritativeUsersMap.set(oEmail, {
                  id: o.userId || oEmail.replace(/[^a-z0-9]/g, '_'),
                  name: o.customerName || oEmail.split('@')[0],
                  email: oEmail,
                  avatar: '',
                  joinedDate: o.date ? String(o.date).split('T')[0] : new Date().toISOString().split('T')[0],
                  totalOrders: 1,
                  totalSpent: Number(o.total) || 0,
                  status: 'Active'
                });
              }
            }
          });
        } catch (e) {}

        const duration = performance.now() - startTime;
        perfTracker.logFetch(USERS_COLLECTION, duration, snapshot.metadata.fromCache ? 'cache' : 'snapshot', snapshot.size);

        notifySubscribers();
      },
      (err) => {
        console.warn('Users snapshot note, falling back to Admin API:', err);
        fetchUsersFromApi().catch(() => {});
      }
    );
  }

  return () => {
    userSubscribers.delete(onUsersUpdate);
    if (userSubscribers.size === 0 && activeUsersUnsub) {
      activeUsersUnsub();
      activeUsersUnsub = null;
      perfTracker.registerListenerStop(USERS_COLLECTION);
    }
  };
}

export async function updateCustomerStatusInFirestore(userId: string, status: 'Active' | 'Blocked'): Promise<void> {
  const isBlocked = status === 'Blocked';
  let apiSucceeded = false;

  // Optimistically update map and notify
  for (const [key, user] of authoritativeUsersMap.entries()) {
    if (user.id === userId || user.email === userId) {
      user.status = status;
      authoritativeUsersMap.set(key, user);
    }
  }
  notifySubscribers();

  // 1. Try backend Admin API first (updates Firestore doc AND Firebase Auth disabled flag)
  try {
    const token = await auth.currentUser?.getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'x-admin-access': 'true',
      'x-rh-admin': 'true'
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`/api/admin/users/${encodeURIComponent(userId)}/status`, {
      method: 'POST',
      headers,
      signal: AbortSignal.timeout(5000),
      body: JSON.stringify({ status })
    });
    if (res.ok) {
      apiSucceeded = true;
    }
  } catch (apiErr) {
    console.warn('API status update note:', apiErr);
  }

  // 2. Direct Firestore update as authoritative database write
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await setDoc(userRef, {
      status,
      accountLocked: isBlocked,
      updatedAt: new Date().toISOString()
    }, { merge: true });
  } catch (err) {
    if (!apiSucceeded) {
      console.error('Error updating customer status in Firestore:', err);
      throw err;
    }
  }
}

export async function saveUserToFirestore(user: CustomerUser): Promise<void> {
  try {
    const isBlocked = user.status === 'Blocked';
    const dataToUpdate: Record<string, any> = {
      status: user.status,
      accountLocked: isBlocked,
      updatedAt: new Date().toISOString()
    };
    if (user.name) dataToUpdate.name = user.name;
    if (user.email) dataToUpdate.email = user.email;
    if (user.avatar) {
      dataToUpdate.avatar = user.avatar;
      dataToUpdate.photoURL = user.avatar;
    }
    await setDoc(doc(db, USERS_COLLECTION, user.id), dataToUpdate, { merge: true });
  } catch (err) {
    console.error('Error saving user to Firestore:', err);
    throw err;
  }
}

export async function deleteUserFromFirestore(userId: string): Promise<void> {
  try {
    await deleteDoc(doc(db, USERS_COLLECTION, userId));
  } catch (err) {
    console.error('Error deleting user from Firestore:', err);
  }
}
